import { Delete, Folder, KeyboardArrowDown, Sell } from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, Menu } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDeletePlaylist } from '~/api/playlists';
import { BulkActionButton, BulkActionMenuItem } from '~/components/bulk-actions';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { focusableDisabledItems } from '~/components/menu-item';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useBulkActionIds } from '~/hooks/unique-id';

export type BulkActionsProps = {
  selectedIds: GridRowSelectionModel;
  onMovePlaylist: () => void;
  onTagPlaylist: () => void;
  canDestroySelectedPlaylists?: boolean;
};

export const BulkActions = ({
  selectedIds,
  onMovePlaylist,
  onTagPlaylist,
  canDestroySelectedPlaylists,
}: BulkActionsProps) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { buttonId, menuId } = useBulkActionIds();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const [deletePlaylist] = useDeletePlaylist();

  if (!selectedIds.length) return null;

  return (
    <>
      <BulkActionButton
        ref={buttonRef}
        disableRipple
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={() => setOpen(true)}
      >
        {t('common.buttons.bulk_actions')}
        <KeyboardArrowDown />
      </BulkActionButton>
      <Menu
        id={menuId}
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
        MenuListProps={{
          ...focusableDisabledItems,
          'aria-labelledby': buttonId,
        }}
      >
        <BulkActionMenuItem
          onClick={() => {
            onMovePlaylist();
            setOpen(false);
          }}
        >
          <ListItemIcon>
            <Folder fontSize="small" />
          </ListItemIcon>
          {t('pages.playlists.list.bulk_actions.move_to_folder')}
        </BulkActionMenuItem>
        <BulkActionMenuItem
          onClick={() => {
            onTagPlaylist();
            setOpen(false);
          }}
        >
          <ListItemIcon>
            <Sell fontSize="small" />
          </ListItemIcon>
          {t('common.tags.add_tag')}
        </BulkActionMenuItem>
        <Divider />
        <BulkActionMenuItem
          tooltip={
            !canDestroySelectedPlaylists
              ? t('pages.playlists.list.bulk_actions.delete_tooltip')
              : ''
          }
          disabled={!canDestroySelectedPlaylists}
          onClick={async () => {
            if (!canDestroySelectedPlaylists) return;
            setOpen(false);
            if (!(await confirmDelete())) return;
            await deletePlaylist({
              variables: {
                playlistIds: selectedIds.map((x) => +x),
              },
            });
          }}
        >
          <ListItemIcon>
            <Delete color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>
            {t('common.actions.delete')}
          </ListItemText>
        </BulkActionMenuItem>
      </Menu>

      <ConfirmDialog
        {...confirmDeleteProps}
        title={t('pages.playlists.list.delete_dialog.title')}
        prompt={
          <Trans i18nKey="pages.playlists.list.bulk_delete_dialog.prompt">
            Deleting these Playlists will remove them from Shows where they are being used.
          </Trans>
        }
        confirm={t('common.dialogs.permanently_delete')}
        cancel={t('common.dialogs.cancel')}
        deleteConfirm
      />
    </>
  );
};
